import { BridgeClient } from 'app/service/bridge/BridgeClient';

export const FACET_CONTRACT_VERSION = 'facets.v1';
export const FACET_FIXTURE_SHA256 = '1915071e318e2028a17ccc78d77d4dd82bd09484afef949a4fecf6121c432a99';

export const FACET_CAPABILITIES = {
  POLICY: 'facet_policy_v1',
  NUMERIC: 'facet_numeric_v1',
  STRING_PRESENTATION: 'facet_string_presentation_v1',
  ORDER: 'facet_order_v1',
  PREVIEW: 'facet_policy_preview_v1',
};

export const FACET_ACTIONS = {
  SCHEMA_GET: 'facets.schema.get',
  CATALOG_LIST: 'facets.catalog.list',
  POLICY_GET: 'facets.policy.get',
  VALUES_SEARCH: 'facets.values.search',
  TYPE_VALIDATE: 'facets.type.validate',
  POLICY_UPDATE: 'facets.policy.update',
  ORDER_GET: 'facets.order.get',
  ORDER_UPDATE: 'facets.order.update',
  POLICY_PREVIEW: 'facets.policy.preview',
};

function asObject(value) {
  return value && typeof value === 'object' && !Array.isArray(value) ? value : {};
}

function asArray(value) {
  return Array.isArray(value) ? value : [];
}

function asString(value) {
  return value === null || value === undefined ? null : String(value);
}

function hasOwn(value, key) {
  return Object.prototype.hasOwnProperty.call(value, key);
}

function normalizeRevision(value) {
  if (value === null || value === undefined || value === '') return value;
  const numeric = Number(value);
  return Number.isSafeInteger(numeric) && numeric >= 0 ? numeric : value;
}

function normalizeMeta(meta) {
  const source = asObject(meta);
  return {
    ...source,
    contract_version: source.contract_version || FACET_CONTRACT_VERSION,
    request_id: asString(source.request_id),
    scope_revision: asString(source.scope_revision),
  };
}

function normalizeCapabilities(capabilities) {
  const source = asObject(capabilities);
  return Object.keys(FACET_CAPABILITIES).reduce((result, key) => {
    const capability = FACET_CAPABILITIES[key];
    result[capability] = source[capability] === true;
    return result;
  }, {});
}

function normalizeVisual(visual) {
  if (visual === null || visual === undefined) return null;
  const source = asObject(visual);
  return {
    kind: source.kind,
    value: source.value,
  };
}

function normalizeGroup(group) {
  const source = asObject(group);
  return {
    ...source,
    value: asString(source.value),
    label: source.label,
    visual: normalizeVisual(source.visual),
  };
}

function normalizeAssignment(assignment) {
  const source = asObject(assignment);
  return {
    ...source,
    source_value_id: asString(source.source_value_id),
    canonical_value: asString(source.canonical_value),
  };
}

function normalizeValuePolicy(valuePolicy) {
  const source = asObject(valuePolicy);
  const normalized = {
    ...source,
    pinned: asArray(source.pinned).map(String),
    hidden: asArray(source.hidden).map(String),
    groups: asArray(source.groups).map(normalizeGroup),
  };
  if (Array.isArray(source.assignments)) {
    normalized.assignments = source.assignments.map(normalizeAssignment);
  }
  if (source.numeric) {
    normalized.numeric = {
      ...source.numeric,
      numeric_definition_revision: normalizeRevision(source.numeric.numeric_definition_revision),
    };
  }
  return normalized;
}

function normalizePolicy(policy) {
  const source = asObject(policy);
  return {
    ...source,
    facet_key: asString(source.facet_key),
    revision: normalizeRevision(source.revision),
    value_policy: normalizeValuePolicy(source.value_policy),
  };
}

function normalizeCatalogData(data) {
  const source = asObject(data);
  return {
    ...source,
    items: asArray(source.items).map((item) => ({
      ...item,
      facet_key: asString(item.facet_key),
      policy_revision: normalizeRevision(item.policy_revision),
    })),
    next_cursor: asString(source.next_cursor),
  };
}

function normalizePolicyData(data) {
  const source = asObject(data);
  return {
    ...source,
    policy: normalizePolicy(source.policy || source),
    scope_revision: asString(source.scope_revision),
    policy_revision: normalizeRevision(source.policy_revision),
  };
}

function normalizeValuesData(data) {
  const source = asObject(data);
  return {
    ...source,
    items: asArray(source.items).map((item) => ({
      ...item,
      source_value_id: asString(item.source_value_id),
      canonical_value: asString(item.canonical_value),
      effective_canonical_value: asString(item.effective_canonical_value),
      pass_through_canonical_value: asString(item.pass_through_canonical_value),
    })),
    next_cursor: asString(source.next_cursor),
    catalog_revision: asString(source.catalog_revision),
  };
}

function normalizeValidationData(data) {
  const source = asObject(data);
  return {
    ...source,
    validation_token: asString(source.validation_token),
    catalog_revision: asString(source.catalog_revision),
  };
}

function normalizeOrderData(data) {
  const source = asObject(data);
  return {
    ...source,
    head: asArray(source.head).map(String),
    tail: asArray(source.tail).map(String),
    scope_revision: asString(source.scope_revision),
  };
}

function sanitizeVisual(visual) {
  if (visual === null || visual === undefined) return null;
  return {
    kind: visual.kind,
    value: visual.value,
  };
}

function sanitizeNumeric(numeric) {
  if (!numeric) return undefined;
  return {
    desired_type: numeric.desired_type,
    fallback_type: numeric.fallback_type,
    numeric_definition_revision: numeric.numeric_definition_revision,
    state: numeric.state,
  };
}

function sanitizeValuePolicy(valuePolicy) {
  const source = asObject(valuePolicy);
  const result = {};
  if (hasOwn(source, 'unmapped')) result.unmapped = source.unmapped;
  if (hasOwn(source, 'sort_remaining_by')) result.sort_remaining_by = source.sort_remaining_by;
  if (hasOwn(source, 'presentation')) result.presentation = source.presentation;
  if (hasOwn(source, 'pinned')) result.pinned = asArray(source.pinned).map(String);
  if (hasOwn(source, 'hidden')) result.hidden = asArray(source.hidden).map(String);
  if (hasOwn(source, 'groups')) {
    result.groups = asArray(source.groups).map((group) => ({
      value: asString(group.value),
      label: group.label,
      visual: sanitizeVisual(group.visual),
    }));
  }
  if (hasOwn(source, 'numeric')) {
    const numeric = sanitizeNumeric(source.numeric);
    if (numeric) result.numeric = numeric;
  }
  return result;
}

function sanitizePolicy(policy) {
  const source = asObject(policy);
  const result = {};
  ['title', 'type', 'visible', 'searchable'].forEach((key) => {
    if (hasOwn(source, key)) result[key] = source[key];
  });
  if (hasOwn(source, 'value_policy')) {
    result.value_policy = sanitizeValuePolicy(source.value_policy);
  }
  return result;
}

function sanitizeAssignmentChange(change) {
  return {
    source_value_id: asString(change.source_value_id),
    canonical_value: asString(change.canonical_value),
  };
}

function sanitizePolicyMutation(payload) {
  const source = asObject(payload);
  return {
    facet_key: asString(source.facet_key),
    expected_revision: normalizeRevision(source.expected_revision),
    client_mutation_id: asString(source.client_mutation_id),
    policy: sanitizePolicy(source.policy),
    assignment_changes: asArray(source.assignment_changes).map(sanitizeAssignmentChange),
    clear_assignment_ids: asArray(source.clear_assignment_ids).map(String),
    delete_group_ids: asArray(source.delete_group_ids).map(String),
    clear_all_assignments: source.clear_all_assignments === true,
    validation_token: asString(source.validation_token),
  };
}

function sanitizeSearchContext(searchContext) {
  const source = asObject(searchContext);
  const result = {};
  ['query', 'filters', 'ranges', 'limit', 'category_id'].forEach((key) => {
    if (Object.prototype.hasOwnProperty.call(source, key)) result[key] = source[key];
  });
  return result;
}

function errorCode(envelope) {
  const source = asObject(envelope);
  const nested = asObject(source.error);
  const data = asObject(source.data);
  return source.code || source.errorCode || nested.code || data.code || 'INTERNAL_ERROR';
}

export class FacetPolicyApiError extends Error {
  constructor(envelope) {
    const source = asObject(envelope);
    const nested = asObject(source.error);
    super(source.message || nested.message || (typeof source.error === 'string' ? source.error : 'Facet policy request failed'));
    this.name = 'FacetPolicyApiError';
    this.code = errorCode(source);
    this.details = source.details || nested.details || asObject(source.data).details || null;
    this.meta = normalizeMeta(source.meta);
    this.envelope = source;
  }
}

function successEnvelope(response, normalizeData) {
  const source = asObject(response);
  if (source.result === 'error') throw new FacetPolicyApiError(source);
  return {
    result: 'success',
    data: normalizeData(asObject(source.data)),
    meta: normalizeMeta(source.meta),
  };
}

export function createBridgeFacetPolicyApi({ client, capabilities } = {}) {
  const bridgeClient = client || new BridgeClient();
  let knownCapabilities = capabilities ? normalizeCapabilities(capabilities) : null;

  function requireCapability(capability) {
    if (knownCapabilities && knownCapabilities[capability] !== true) {
      throw new FacetPolicyApiError({
        result: 'error',
        code: 'CAPABILITY_MISSING',
        message: `Required capability is unavailable: ${capability}`,
        details: { capability },
        meta: { contract_version: FACET_CONTRACT_VERSION },
      });
    }
  }

  async function send(action, payload, normalizeData, capability) {
    if (capability) requireCapability(capability);
    try {
      const response = await bridgeClient.sendMessageWithResponse(action, payload);
      return successEnvelope(response, normalizeData);
    } catch (error) {
      if (error instanceof FacetPolicyApiError) throw error;
      throw new FacetPolicyApiError(error);
    }
  }

  return {
    supports(capability) {
      return knownCapabilities === null ? null : knownCapabilities[capability] === true;
    },

    getCapabilities() {
      return knownCapabilities ? { ...knownCapabilities } : null;
    },

    async getSchema() {
      const response = await send(
        FACET_ACTIONS.SCHEMA_GET,
        {},
        (data) => ({
          ...data,
          scope_revision: asString(data.scope_revision),
          capabilities: normalizeCapabilities(data.capabilities),
        }),
      );
      knownCapabilities = response.data.capabilities;
      return response;
    },

    listCatalog(params = {}) {
      requireCapability(FACET_CAPABILITIES.POLICY);
      return send(FACET_ACTIONS.CATALOG_LIST, {
        query: params.query || '',
        type: params.type === undefined ? null : params.type,
        issues_only: params.issues_only === true,
        cursor: params.cursor === undefined ? null : params.cursor,
        limit: params.limit === undefined ? 50 : params.limit,
      }, normalizeCatalogData);
    },

    getPolicy(params = {}) {
      return send(FACET_ACTIONS.POLICY_GET, {
        facet_key: asString(params.facet_key),
      }, normalizePolicyData, FACET_CAPABILITIES.POLICY);
    },

    searchValues(params = {}) {
      return send(FACET_ACTIONS.VALUES_SEARCH, {
        facet_key: asString(params.facet_key),
        query: params.query || '',
        assignment: params.assignment || 'all',
        canonical_value: params.canonical_value === undefined ? null : params.canonical_value,
        cursor: params.cursor === undefined ? null : params.cursor,
        limit: params.limit === undefined ? 100 : params.limit,
      }, normalizeValuesData, FACET_CAPABILITIES.POLICY);
    },

    validateType(params = {}) {
      const required = params.target_type === 'number'
        ? FACET_CAPABILITIES.NUMERIC
        : FACET_CAPABILITIES.POLICY;
      return send(FACET_ACTIONS.TYPE_VALIDATE, {
        facet_key: asString(params.facet_key),
        target_type: params.target_type,
      }, normalizeValidationData, required);
    },

    updatePolicy(payload) {
      const policy = asObject(payload && payload.policy);
      const valuePolicy = asObject(policy.value_policy);
      const required = hasOwn(valuePolicy, 'presentation')
        ? FACET_CAPABILITIES.STRING_PRESENTATION
        : (policy.type === 'number' ? FACET_CAPABILITIES.NUMERIC : FACET_CAPABILITIES.POLICY);
      return send(FACET_ACTIONS.POLICY_UPDATE, sanitizePolicyMutation(payload), normalizePolicyData, required);
    },

    getOrder() {
      return send(FACET_ACTIONS.ORDER_GET, {}, normalizeOrderData, FACET_CAPABILITIES.ORDER);
    },

    updateOrder(payload = {}) {
      return send(FACET_ACTIONS.ORDER_UPDATE, {
        expected_scope_revision: asString(payload.expected_scope_revision),
        client_mutation_id: asString(payload.client_mutation_id),
        head: asArray(payload.head).map(String),
        tail: asArray(payload.tail).map(String),
      }, normalizeOrderData, FACET_CAPABILITIES.ORDER);
    },

    previewPolicy(payload = {}) {
      return send(FACET_ACTIONS.POLICY_PREVIEW, {
        ...sanitizePolicyMutation(payload),
        search_context: sanitizeSearchContext(payload.search_context),
      }, (data) => ({
        ...data,
        scope_revision: asString(data.scope_revision),
      }), FACET_CAPABILITIES.PREVIEW);
    },
  };
}
